import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import {styled} from "@mui/material";
import {useDialog} from "app/providers/dialog";
import {PageTab} from "app/routes/entity-administration/tabs";
import {CanceledError} from 'axios';
import Guid from "common/values/guid/guid";
import EntityMemberAPIService from "legal-entities/entities/entity-member/api/entity-member-api-service";
import EntityMember from "legal-entities/entities/entity-member/entity-member";
import LegalEntity from "legal-entities/entities/legal-entity/legal-entity";
import ViewIndividualProfile from "marketplace/values/individual-profile/view/view-individual-profile";
import IndividualAvatar from "marketplace/view/individual-avatar";
import {
  createMRTColumnHelper,
  MaterialReactTable,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
  MRT_TableInstance,
  useMaterialReactTable
} from "material-react-table";
import * as React from "react";
import {useEffect} from "react";
import {Link as RouterLink, useLoaderData, useNavigate} from "react-router-dom";
import {useSession} from "users/session/session-context";

const NameCellContainer = styled(Box)(() => ({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center'
}));
const NameText = styled(ListItemText)(({ theme }) => ({
  marginLeft: theme.spacing(1)
}));
const RowTitle = styled(Typography)(() => ({
  fontWeight: "500",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));


type EntityMembersTable = {
  renderTopToolbarCustomActions: (props: { table: MRT_TableInstance<EntityMember> }) => React.ReactNode;
};

export default function EntityMembersTable(
  props: Readonly<EntityMembersTable>
) {
  const { renderTopToolbarCustomActions } = props;
  const [isLoading, setIsLoading] = React.useState(false);
  const [isRefetching, setIsRefetching] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [data, setData] = React.useState<EntityMember[]>([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [wasErrorLoading, setWasErrorLoading] = React.useState(false);
  const [columnFilters, setColumnFilters] =
    React.useState<MRT_ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<MRT_SortingState>([]);
  const [pagination, setPagination] = React.useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const routeParams = useLoaderData();
  const navigate = useNavigate();
  const session = useSession();
  const { openDialog } = useDialog();

  // Handle URL changes
  useEffect(() => {
    if (!session.user?.companyEntityId || !session.user?.isCompanyManager) return;

    let abortController = new AbortController();
    void fetchData(routeParams.tab as PageTab, abortController);

    return () => {
      abortController.abort();
      abortController = new AbortController();
    }
  }, [routeParams]);

  const fetchData = async (_tab?: PageTab, abortController?: AbortController) => {
    if (!session?.hasEntity) {
      setData([]);
      setRowCount(0);
      setIsLoading(false);
      setIsRefetching(false);
      setWasErrorLoading(true);
      return;
    }

    setIsLoading(!data.length);

    try {
      const firstEntity = new LegalEntity(
        session.entities[0].entityId,
        session.entities[0].entityName
      );
      const entityMemberService = new EntityMemberAPIService(session);
      const members =
        await entityMemberService.getLegalEntityMembersUserInfo(
          firstEntity,
          abortController
        );
      setData(members);
      setRowCount(members.length);
      setIsLoading(false);
      setIsRefetching(false);
      setWasErrorLoading(false);
    } catch (error) {
      if (error instanceof CanceledError) return;
      console.error(error);
      setData([]);
      setRowCount(0);
      setWasErrorLoading(true);
      setIsLoading(false);
      setIsRefetching(false);
    }

    setWasErrorLoading(false);
    setIsLoading(false);
    setIsRefetching(false);
  };

  function handleViewProfile(individualId?: Guid): void {
    if (!individualId) return;

    const parentPath = new URL(
      ".",
      globalThis.location.origin + globalThis.location.pathname
    );

    openDialog(
      {
        titleStyle: {
          position: "absolute",
          right: 0,
          top: 0,
        },
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        MuiProps: {
          maxWidth: "lg",
          fullWidth: false,
        },
        component: (
          <ViewIndividualProfile
            individualId={individualId}
          />
        ),
      },
      () => navigate(parentPath.pathname)
    );
  }

  function renderNameColumn(data: EntityMember): React.ReactNode {
    const member: EntityMember = data;

    let name = `${member.name?.firstName} ${member.name?.lastName}`;
    let email = member.email.value;

    const contactLink = (
      <RowTitle>{name}</RowTitle>
    );

    return (
      <NameCellContainer
        onClick={() => handleViewProfile(member.individualId)}
      >
        <IndividualAvatar
          avatarId={member.avatarId}
          individualId={member.individualId}
          session={session}
        />
        <NameText
          primary={contactLink}
          secondary={email}
        />
      </NameCellContainer>
    );
  }

  function renderRoleColumn(data: EntityMember) {
    const entityMember: EntityMember = data;
    if (entityMember.isOfficer) return 'Officer';
    if (entityMember.isAdmin) return 'Admin';
    return 'Member';
  }

  function renderPermissionsColumn(data: EntityMember) {
    return <Link
      component={RouterLink}
      to={{
        pathname: '/administration/permissions/',
        search: `?granteeUserId=${data.userId}`
      }}
    >
      View Permissions
    </Link>
  }

  const membersColumnHelper = createMRTColumnHelper<EntityMember>();
  let columns = [
    membersColumnHelper.accessor(renderNameColumn, {
      header: "Name",
      id: "name",
    }),
    membersColumnHelper.accessor(renderRoleColumn, {
      header: "Role",
      id: "isAdmin",
    }),
    membersColumnHelper.accessor(renderPermissionsColumn, {
      header: "Permissions",
      id: "permissions",
    }),
    membersColumnHelper.accessor((member: EntityMember) => member.created?.toDateString(), {
      header: "Registered",
      id: "registered",
    })
  ];

  const table = useMaterialReactTable({
    columns,
    data,
    enableRowSelection: false,
    enableTableHead: true,
    enableRowActions: false,
    enableColumnPinning: true,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
    },
    getRowId: (row) => row.id?.toString() ?? Guid.generate().toString(),
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnFilters: true,
    manualPagination: true,
    manualSorting: true,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setQuery,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    renderTopToolbarCustomActions: renderTopToolbarCustomActions,
    muiTableContainerProps: ({ table }) => ({
      sx: {
        height: `
        }}calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiToolbarAlertBannerProps: wasErrorLoading
      ? {
        color: "error",
        children: "Error loading data",
      }
      : undefined,
    rowCount,
    state: {
      columnFilters,
      globalFilter: query,
      isLoading,
      pagination,
      showAlertBanner: wasErrorLoading,
      showProgressBars: isRefetching,
      sorting,
    },
  });

  return <MaterialReactTable table={table} />;
}
